import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import CertificateOrganizerDataDownloadForm from '../thumbnail/CertificateOrganizerDataDownloadForm';
import CertificateUserDataDownloadForm from '../thumbnail/CertificateUserDataDownloadForm';
import CustomDialog from '../dialogs/CustomDialog';
import GenerateEventCertificateMutation from '../../../mutations/GenerateEventCertificateMutation';
import environment from '../../../environment';
import { BERLIN_MARATHON_EVENTID } from '../../../utils/variables';
import AccountDownloadDialogTile from './AccountDownloadDialogTile';
import AccountDownloadTileButton from './AccountDownloadTileButton';
import { maybeExtractCombinedBib } from '../../../utils/common';

const AccountDownloadTileCertificate = (props) => {
  const {
    photoNodeId,
    eventSgId,
    onScrollToMedia,
    originalsAreAvailable,
    icon,
    textInSpan,
    type,
    isFaceSearchEvent,
    startNumbers,
    isUserInputForCertificateRequired,
    isAdditionalConsentForCertificateRequired,
    certificateOrganizerUrl,
  } = props;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [failed, setFailed] = useState(false);

  const isPrivacyConsentNeeded =
    eventSgId === BERLIN_MARATHON_EVENTID || isAdditionalConsentForCertificateRequired;
  const isAvailableOnlyOnOrganizerSite = Boolean(certificateOrganizerUrl);

  const { bibnumber } = useParams();
  const formattedStartNumber = maybeExtractCombinedBib(bibnumber);

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleDownloadCertificate = (
    startNumber,
    category,
    firstName,
    lastName,
    time
  ) => {
    addToPendingArray(startNumber);
    GenerateEventCertificateMutation.commit(
      environment,
      eventSgId,
      photoNodeId,
      startNumber,
      category,
      firstName,
      lastName,
      time,
      (error, url) => {
        if (!error) {
          setFailed(false);
          download(url);
        } else {
          setFailed(true);
        }
        removeFromPendingArray(startNumber);
      }
    );
  };

  const download = (url) => {
    window.location.href = url;
  };

  const addToPendingArray = (startNumber) => {
    let newArr = [...pendingRequests];
    newArr.push(startNumber);
    setPendingRequests(newArr);
  };

  const removeFromPendingArray = (startNumber) => {
    const newArr = pendingRequests.filter((item) => item !== startNumber);
    setPendingRequests(newArr);
  };

  // additional check if certificate is available
  if (startNumbers.length > 0 && !(startNumbers.includes(bibnumber) || startNumbers.includes(maybeExtractCombinedBib(bibnumber)))) {
    return null;
  }

  if ((!photoNodeId || startNumbers.length === 0 || !originalsAreAvailable) || (photoNodeId && isAvailableOnlyOnOrganizerSite)) {
    // download area instructions
    // thumbnail preview organizer's site redirection instruction
    return (
      <AccountDownloadDialogTile
        certificateOrganizerUrl={certificateOrganizerUrl}
        icon={icon}
        textInSpan={textInSpan}
        type={type}
        onScrollToMedia={onScrollToMedia}
        notActive={
          (startNumbers.length === 0 || !originalsAreAvailable) &&
          !isAvailableOnlyOnOrganizerSite
        }
        certificateProductIsNotReady={startNumbers.length === 0}
        originalsAreAvailable={originalsAreAvailable}
      />
    );
  }

  // thumbnail preview download buttons
  return (
    <div className="thumbnail-btn-wrapper">
      <AccountDownloadTileButton
        icon={icon}
        textInSpan={textInSpan}
        onClick={() => {
          if (
            isFaceSearchEvent ||
            isPrivacyConsentNeeded ||
            isUserInputForCertificateRequired
          ) {
            setDialogOpen(true);
          } else {
            handleDownloadCertificate(bibnumber);
          }
        }}
        isPending={pendingRequests.length > 0}
        error={failed}
      />
      <CustomDialog open={dialogOpen} onRequestClose={closeDialog}>
        {isUserInputForCertificateRequired && (
          <CertificateUserDataDownloadForm
            bibnumber={bibnumber}
            onDownloadCertificate={handleDownloadCertificate}
            pendingRequests={pendingRequests}
            failed={failed}
            startNumbers={startNumbers}
            isFaceSearchEvent={isFaceSearchEvent}
          />
        )}
        {!isUserInputForCertificateRequired && (
          <CertificateOrganizerDataDownloadForm
            onDownloadCertificate={handleDownloadCertificate}
            pendingRequests={pendingRequests}
            failed={failed}
            isPrivacyConsentNeeded={isPrivacyConsentNeeded}
            bibnumber={formattedStartNumber}
            startNumbers={startNumbers}
            isFaceSearchEvent={isFaceSearchEvent}
          />
        )}
      </CustomDialog>
    </div>
  );
};

AccountDownloadTileCertificate.propTypes = {
  photoNodeId: PropTypes.string,
  isFaceSearchEvent: PropTypes.bool,
  eventSgId: PropTypes.number,
  type: PropTypes.string,
  textInSpan: PropTypes.object,
  icon: PropTypes.object,
  onScrollToMedia: PropTypes.func,
  originalsAreAvailable: PropTypes.bool,
  startNumbers: PropTypes.array,
  isUserInputForCertificateRequired: PropTypes.bool,
  isAdditionalConsentForCertificateRequired: PropTypes.bool,
  certificateOrganizerUrl: PropTypes.string,
};

export default AccountDownloadTileCertificate;
export { AccountDownloadTileCertificate };
